import { h } from 'preact';
import { useState, useEffect } from 'preact/hooks';

// Captures the beforeinstallprompt event so we can trigger it on demand.
// Also detects iOS where the event never fires.

let _deferredPrompt = null;

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    _deferredPrompt = e;
    // Dispatch a custom event so any mounted banner can react
    window.dispatchEvent(new Event('pwa-installable'));
  });
}

function isIOS() {
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
}

function isInStandaloneMode() {
  return window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true;
}

function isInstallable() {
  return !!_deferredPrompt || isIOS();
}

const DISMISSED_KEY = 'pwa-banner-dismissed';

export default function InstallBanner() {
  const [show, setShow] = useState(false);
  const [ios, setIos] = useState(false);
  const [installing, setInstalling] = useState(false);

  useEffect(() => {
    if (isInStandaloneMode()) return; // Already installed
    if (sessionStorage.getItem(DISMISSED_KEY)) return;

    const ios_ = isIOS();
    setIos(ios_);

    if (ios_ || _deferredPrompt) {
      setShow(true);
    }

    const handler = () => setShow(true);
    window.addEventListener('pwa-installable', handler);
    return () => window.removeEventListener('pwa-installable', handler);
  }, []);

  const handleInstall = async () => {
    if (!_deferredPrompt) return;
    setInstalling(true);
    try {
      await _deferredPrompt.prompt();
      const { outcome } = await _deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        _deferredPrompt = null;
        setShow(false);
      }
    } finally {
      setInstalling(false);
    }
  };

  const handleDismiss = () => {
    sessionStorage.setItem(DISMISSED_KEY, '1');
    setShow(false);
  };

  if (!show) return null;

  return (
    <div style="position:fixed;bottom:16px;left:50%;transform:translateX(-50%);z-index:9999;width:calc(100% - 32px);max-width:480px;pointer-events:auto">
      <div style="background:var(--color-surface);border:1px solid var(--color-border);border-radius:16px;padding:16px 18px;box-shadow:0 8px 32px rgba(0,0,0,.35);display:flex;align-items:flex-start;gap:14px">
        {/* App icon */}
        <div style="width:44px;height:44px;border-radius:12px;background:linear-gradient(135deg,#6366f1,#8b5cf6);display:flex;align-items:center;justify-content:center;font-size:22px;flex-shrink:0">
          🏆
        </div>

        {/* Text */}
        <div style="flex:1;min-width:0">
          <div style="font-size:13px;font-weight:700;margin-bottom:3px">VerenigingsAPP installeren</div>
          {ios ? (
            <div style="font-size:12px;color:var(--color-text-muted);line-height:1.5">
              Tik op <strong>Delen</strong> <span style="font-size:14px">⬆</span> en kies <strong>"Zet op beginscherm"</strong> om de app te installeren.
            </div>
          ) : (
            <div style="font-size:12px;color:var(--color-text-muted);line-height:1.5">
              Installeer de app voor snelle toegang — werkt ook offline.
            </div>
          )}

          {/* Actions */}
          {!ios && (
            <div style="display:flex;gap:8px;margin-top:10px">
              <button
                onClick={handleInstall}
                disabled={installing}
                style="padding:7px 16px;background:var(--color-primary);color:#fff;border:none;border-radius:8px;font-size:12px;font-weight:600;cursor:pointer;transition:opacity .15s"
              >
                {installing ? 'Installeren...' : '📲 Installeren'}
              </button>
              <button
                onClick={handleDismiss}
                style="padding:7px 12px;background:transparent;color:var(--color-text-muted);border:1px solid var(--color-border);border-radius:8px;font-size:12px;cursor:pointer"
              >
                Niet nu
              </button>
            </div>
          )}
          {ios && (
            <button
              onClick={handleDismiss}
              style="margin-top:8px;padding:6px 12px;background:transparent;color:var(--color-text-muted);border:1px solid var(--color-border);border-radius:8px;font-size:12px;cursor:pointer"
            >
              Sluiten
            </button>
          )}
        </div>

        {/* Close X */}
        <button
          onClick={handleDismiss}
          style="padding:4px;background:transparent;border:none;color:var(--color-text-muted);cursor:pointer;font-size:18px;line-height:1;flex-shrink:0"
          aria-label="Sluiten"
        >
          ×
        </button>
      </div>
    </div>
  );
}
